import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as env } from '../../environments/environment';
import { OrderType } from './orders.service';

export enum PreparationStatus {
  Queued = 'queued',
  Pending = 'pending',
  InProgress = 'in_progress',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

// Mirror du prédicat serveur `countActiveSendings` : ce qui reste à préparer.
export const ACTIVE_KITCHEN_STATUSES: PreparationStatus[] = [
  PreparationStatus.Queued,
  PreparationStatus.Pending,
  PreparationStatus.InProgress,
];

export interface PreparationTicketItem {
  productId: string;
  categoryFormatId?: string;
  formatName?: string;
  name: string;
  quantity: number;
  comment: string;
  isOffered?: boolean;
  orderProductIndex?: number;
  isCancelled?: boolean;
  options?: PreparationTicketItemOption[];
  /** An option prepared at another station than its product: it travels as an item of its own. */
  isOption?: boolean;
  parentName?: string;
}

export interface PreparationTicketItemOption {
  label: string;
  quantity: number;
}

export function ticketItemOptionLabels(item: Pick<PreparationTicketItem, 'options'>): string[] {
  return (item.options ?? []).map((option) =>
    option.quantity > 1 ? `${option.quantity} x ${option.label}` : option.label
  );
}

export interface PreparationTicketResponse {
  _id: string;
  restaurantId: string;
  orderId: string;
  orderType: OrderType;
  tableObjectId?: string | null;
  tableLabel?: string | null;
  level: number;
  functionalCategoryId: string;
  functionalCategoryLabel: string;
  items: PreparationTicketItem[];
  status: PreparationStatus;
  queuedUntil?: string | null;
  /** Counter sale handed over on the spot: created completed, never queued for a station. */
  directHandover?: boolean;
  releasedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PreparationTicketsService {
  private readonly apiPath = '/preparation-tickets';

  constructor(private http: HttpClient) {}

  getTickets(params: {
    restaurantId: string;
    status?: PreparationStatus;
    functionalCategoryId?: string;
    orderId?: string;
  }): Observable<PreparationTicketResponse[]> {
    const query = new URLSearchParams();
    query.append('restaurantId', params.restaurantId);
    if (params.status) {
      query.append('status', params.status);
    }
    if (params.functionalCategoryId) {
      query.append('functionalCategoryId', params.functionalCategoryId);
    }
    if (params.orderId) {
      query.append('orderId', params.orderId);
    }

    return this.http.get<PreparationTicketResponse[]>(
      `${env.apiUrl}${this.apiPath}?${query.toString()}`
    );
  }

  updateStatus(id: string, status: PreparationStatus): Observable<PreparationTicketResponse> {
    return this.http.patch<PreparationTicketResponse>(`${env.apiUrl}${this.apiPath}/${id}/status`, {
      status,
    });
  }
}
